import { ChevronRight, Plus, User } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  canOpenRepair,
  formatPlainDate,
  isRepairClosed,
  REPAIR_STATUS_LABELS,
  today,
} from '@cfl/assets-shared';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { WrenchArt } from '@/components/illustrations';
import { AssetPicker } from '@/components/pickers';
import { PageContainer } from '@/components/shell/page-container';
import { PILL_CLASS, repairStatusVariant } from '@/lib/badges';
import { ApiError } from '@/lib/api';
import { useAssets, type AssetSummary } from '@/lib/assets-api';
import { useCloseRepair, useOpenRepair, useRepairs, type Repair } from '@/lib/operations-api';
import { useCan } from '@/lib/session';
import { StatTile } from '@/components/stat-tile';
import { useRepairSummary } from '@/lib/operations-api';
import { RepairQueue } from '@/routes/repair-queue';

export function RepairsPage() {
  const canManage = useCan('manageRepairs');
  const { data: repairs, isPending } = useRepairs();
  const { data: summary } = useRepairSummary();
  const [opening, setOpening] = React.useState(false);
  const [closing, setClosing] = React.useState<Repair | null>(null);

  const open = repairs?.filter((r) => r.status === 'OPEN' || r.status === 'IN_PROGRESS') ?? [];
  const closed = repairs?.filter((r) => r.status === 'COMPLETED' || r.status === 'CANCELLED') ?? [];

  return (
    <PageContainer>
      <div className="grid gap-4 sm:grid-cols-3">
        <StatTile
          label="Open"
          value={summary?.open ?? 0}
          caption={summary?.open ? 'with a vendor right now' : 'nothing out for repair'}
          tone={summary?.open ? 'warning' : 'good'}
        />
        <StatTile
          label="All time"
          value={summary?.total ?? 0}
          caption="repairs ever recorded"
        />
        <StatTile
          label="Total spent"
          value={summary?.totalCost ?? '0.00'}
          caption="across every repair recorded"
        />
      </div>

      {canManage && (
        <div>
          <Button hint={"Record a repair or service"} size="sm" onClick={() => setOpening(true)}>
            <Plus className="mr-1.5 size-4" /> Log a repair
          </Button>
        </div>
      )}

      <RepairQueue />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">In progress</CardTitle>
          <CardDescription className="text-xs">
            While a repair is open the asset is out of service and cannot be issued.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {isPending ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : open.length === 0 ? (
            <EmptyState art={WrenchArt} title="Nothing is out for repair" body="When someone reports a fault or IT sends a device off, it is tracked here until it comes back." />
          ) : (
            <ul className="divide-y">
              {open.map((repair) => (
                <RepairRow
                  key={repair.id}
                  repair={repair}
                  action={
                    canManage ? (
                      <Button hint={"Close this repair and record the outcome"} size="sm" variant="secondary" onClick={() => setClosing(repair)}>
                        Close
                      </Button>
                    ) : null
                  }
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Shut by default, like the settled reports above it: finished repairs are
          looked up, not read down. */}
      {closed.length > 0 && (
        <Card>
          <details className="group">
            <summary className="flex cursor-pointer list-none items-center gap-2 p-6 text-sm font-semibold [&::-webkit-details-marker]:hidden">
              <ChevronRight className="size-4 text-muted-foreground transition-transform group-open:rotate-90" />
              History
              <Badge variant="soft-muted" className={PILL_CLASS}>
                {closed.length}
              </Badge>
            </summary>
            <CardContent className="pt-0">
              <ul className="divide-y">
                {closed.map((repair) => (
                  <RepairRow key={repair.id} repair={repair} action={null} />
                ))}
              </ul>
            </CardContent>
          </details>
        </Card>
      )}

      <OpenRepairDialog open={opening} onOpenChange={setOpening} />
      <CloseRepairDialog repair={closing} onClose={() => setClosing(null)} />
    </PageContainer>
  );
}

function RepairRow({ repair, action }: { repair: Repair; action: React.ReactNode }) {
  return (
    <li className="flex flex-wrap items-center gap-3 py-3">
      <div className="min-w-0 flex-1">
        <Link to={`/assets/${repair.asset.id}`} className="text-sm font-medium hover:underline">
          {repair.asset.name}
        </Link>
        {/* Only while the repair is open: the holder is read live, so on a closed
            row it would name whoever has the asset today, not who had it then. */}
        {repair.holder && !isRepairClosed(repair.status) && (
          <span className="ml-2 inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 align-middle text-xs font-medium">
            <User className="size-3.5 text-muted-foreground" />
            <Link to={`/employees/${repair.holder.id}`} className="hover:underline">
              {repair.holder.name}
            </Link>
            <span className="at-num text-muted-foreground">{repair.holder.employeeCode}</span>
          </span>
        )}
        <div className="at-num text-xs text-muted-foreground">
          {repair.asset.code} · started {formatPlainDate(repair.startedAt)}
          {repair.completedAt ? ` · closed ${formatPlainDate(repair.completedAt)}` : ''}
        </div>
        <div className="mt-0.5 text-xs">
          {repair.reason}
          {repair.vendor ? ` · ${repair.vendor}` : ''}
          {repair.cost ? ` · ${repair.cost}` : ''}
        </div>
      </div>
      <Badge variant={repairStatusVariant(repair.status)} className={PILL_CLASS}>
        {REPAIR_STATUS_LABELS[repair.status]}
      </Badge>
      {action}
    </li>
  );
}

/**
 * Logging a repair, from the queue or from one asset.
 *
 * `asset` set means it was opened against a known asset - the screen that shows its
 * repair history is the obvious place to add to it - so there is no picker, and no
 * thousand-row fetch to fill one.
 */
export function OpenRepairDialog({
  asset,
  open,
  onOpenChange,
}: {
  asset?: Pick<AssetSummary, 'id' | 'code' | 'name'>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const openRepair = useOpenRepair();
  // Anything not already archived can go for repair - including something still out
  // with an employee, which is the laptop-goes-to-the-vendor case.
  const { data: assets } = useAssets({ pageSize: 1000 }, { enabled: asset === undefined });
  // Anything already at the vendor would be refused on submit (REPAIR_ALREADY_OPEN),
  // so it is not offered. Assets still out with someone ARE offered - the laptop
  // going to the vendor while the employee keeps the assignment is a real case.
  const [form, setForm] = React.useState({
    assetId: asset?.id ?? '',
    startedAt: today(),
    reason: '',
    problem: '',
    vendor: '',
    cost: '',
  });

  React.useEffect(() => {
    if (open) {
      setForm({
        assetId: asset?.id ?? '',
        startedAt: today(),
        reason: '',
        problem: '',
        vendor: '',
        cost: '',
      });
    }
  }, [open, asset?.id]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const result = await openRepair.mutateAsync({
        assetId: form.assetId,
        startedAt: form.startedAt,
        reason: form.reason.trim(),
        problem: form.problem.trim() || null,
        vendor: form.vendor.trim() || null,
        cost: form.cost.trim() === '' ? null : Number(form.cost),
      });
      toast.success(`${result.assetCode} sent for repair.`);
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That could not be recorded.');
    }
  };

  const set = (key: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {asset ? (
              <>
                Log a repair on <span className="at-num">{asset.code}</span>
              </>
            ) : (
              'Log a repair'
            )}
          </DialogTitle>
          <DialogDescription>
            {asset ? `${asset.name} — this takes it` : 'This takes the asset'} out of service.
            Where it goes when the repair closes is remembered — an asset still issued to
            someone stays issued to them.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {/* No picker when the asset is already settled — the title names it. */}
          {!asset && (
            <div className="space-y-1.5">
              <Label>Asset</Label>
              <AssetPicker
                assets={assets?.assets.filter((a) => canOpenRepair(a.status))}
                value={form.assetId}
                onChange={(v) => set('assetId', v)}
                showHolder
              />
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Started</Label>
              <Input
                type="date"
                value={form.startedAt}
                max={today()}
                onChange={(e) => set('startedAt', e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Vendor</Label>
              <Input value={form.vendor} onChange={(e) => set('vendor', e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Reason</Label>
            <Input
              value={form.reason}
              onChange={(e) => set('reason', e.target.value)}
              placeholder="Screen replacement"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label>Problem description</Label>
            <Textarea rows={2} value={form.problem} onChange={(e) => set('problem', e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Estimated cost</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              className="sm:w-[200px]"
              value={form.cost}
              onChange={(e) => set('cost', e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button hint={"Log the repair"} type="submit" disabled={openRepair.isPending}>
              {openRepair.isPending ? 'Saving…' : 'Log repair'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CloseRepairDialog({ repair, onClose }: { repair: Repair | null; onClose: () => void }) {
  const close = useCloseRepair(repair?.id ?? '');
  const [form, setForm] = React.useState({
    status: 'COMPLETED' as 'COMPLETED' | 'CANCELLED',
    completedAt: today(),
    cost: '',
    parts: '',
    remarks: '',
    resultingStatus: '' as '' | 'AVAILABLE' | 'DAMAGED' | 'RETIRED' | 'DISPOSED',
  });

  React.useEffect(() => {
    if (repair) {
      setForm({
        status: 'COMPLETED',
        completedAt: today(),
        cost: repair.cost ?? '',
        parts: repair.parts ?? '',
        remarks: '',
        resultingStatus: '',
      });
    }
  }, [repair]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!repair) return;
    try {
      const result = await close.mutateAsync({
        status: form.status,
        completedAt: form.completedAt,
        cost: form.cost.trim() === '' ? null : Number(form.cost),
        parts: form.parts.trim() || null,
        remarks: form.remarks.trim() || null,
        resultingStatus: form.resultingStatus || undefined,
      });
      toast.success(`${result.assetCode} is now ${result.status.replace(/_/g, ' ').toLowerCase()}.`);
      onClose();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That could not be recorded.');
    }
  };

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Dialog open={repair !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Close repair</DialogTitle>
          <DialogDescription>
            {repair?.asset.code} — {repair?.reason}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Outcome</Label>
              <Select
                value={form.status}
                onValueChange={(v) => set('status', v as 'COMPLETED' | 'CANCELLED')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Date closed</Label>
              <Input
                type="date"
                value={form.completedAt}
                onChange={(e) => set('completedAt', e.target.value)}
                required
              />
            </div>
          </div>

          {form.status === 'COMPLETED' ? (
            <div className="space-y-1.5">
              <Label>Where does the asset go?</Label>
              <Select
                value={form.resultingStatus || 'default'}
                onValueChange={(v) =>
                  set('resultingStatus', v === 'default' ? '' : (v as 'AVAILABLE'))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Back where it was before the repair</SelectItem>
                  <SelectItem value="AVAILABLE">Available</SelectItem>
                  <SelectItem value="DAMAGED">Still damaged</SelectItem>
                  <SelectItem value="RETIRED">Retired</SelectItem>
                  <SelectItem value="DISPOSED">Disposed of</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                A repair that did not fix the problem should not put the asset back in the
                pool. If it was issued to someone and never given back, leaving this on the
                default returns it to them.
              </p>
            </div>
          ) : (
            <p className="rounded-md bg-muted/60 p-2.5 text-[11px] text-muted-foreground">
              Cancelled means the repair never happened — a job raised in error, or a quote
              declined — so the asset goes back exactly where it was.
            </p>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Final cost</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.cost}
                onChange={(e) => set('cost', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Parts replaced</Label>
              <Input value={form.parts} onChange={(e) => set('parts', e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Remarks</Label>
            <Textarea rows={2} value={form.remarks} onChange={(e) => set('remarks', e.target.value)} />
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button hint={"Close the repair and record the outcome"} type="submit" disabled={close.isPending}>
              {close.isPending ? 'Saving…' : 'Close repair'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
